// TanStack Query hooks for the subsidy office report import ("Abrechnung
// gemaess Auszahlung"): the office's own settlement sheet, parsed server-side
// and overlaid on the Subsidy Matching tab.
//
// Query-key factories follow the subsidyMatchingKeys pattern, and every key
// carries the active orgId (frontend.md: tenant-scoped query keys MUST include
// the active orgId) so a cache entry can never cross tenants.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { QueryClient } from "@tanstack/react-query";
import { useAuth } from "@clerk/clerk-react";
import { useLingui } from "@lingui/react";
import { msg } from "@lingui/macro";
import { toast } from "sonner";
import { ReconciliationService } from "@/api";
import type {
  reconciliation_SubsidyOfficeReportDetailResponse,
  reconciliation_SubsidyOfficeReportListResponse,
  reconciliation_SubsidyRoundingAdoptedRowDTO,
  reconciliation_SubsidyRoundingAdoptionResponse,
  reconciliation_SubsidyRoundingSkippedRowDTO,
} from "@/api";
import { backendErrorText } from "@/lib/translateBackendError";
import { invalidateMoneyViews } from "./invalidateMoneyViews";
import { subsidyMatchingKeys } from "./useSubsidyMatching";
import { subsidyReportKeys } from "@/hooks/useSubsidyReports";
import { workflowKeys } from "@/hooks/useWorkflows";

/**
 * A link or an adoption by an administrator raises Sozialdepartement items on
 * the server (issue #2005), so the queue caches are refreshed after both.
 */
function invalidateSubsidyQueue(queryClient: QueryClient) {
  queryClient.invalidateQueries({ queryKey: subsidyReportKeys.all });
  queryClient.invalidateQueries({ queryKey: workflowKeys.lists() });
  queryClient.invalidateQueries({ queryKey: workflowKeys.pendingCount() });
  queryClient.invalidateQueries({ queryKey: workflowKeys.stats() });
}

export const subsidyOfficeReportKeys = {
  all: ["reconciliation", "subsidy-office-reports"] as const,
  forMonth: (
    orgId: string | null,
    officeId: string | null,
    year: number | null,
    month: number | null,
  ) =>
    [
      ...subsidyOfficeReportKeys.all,
      "month",
      orgId,
      officeId,
      year,
      month,
    ] as const,
  detail: (orgId: string | null, id: string) =>
    [...subsidyOfficeReportKeys.all, "detail", orgId, id] as const,
  /**
   * The rounding adoption the last upload, attach or adopt call returned for a
   * statement (issue #1576). Written by those mutations only; never fetched.
   */
  adoption: (orgId: string | null, reportId: string) =>
    [...subsidyOfficeReportKeys.all, "adoption", orgId, reportId] as const,
};

/** What a rounding adoption did on one statement: rows adopted and rows left. */
export interface SubsidyReportAdoption {
  adopted: reconciliation_SubsidyRoundingAdoptedRowDTO[];
  skipped: reconciliation_SubsidyRoundingSkippedRowDTO[];
  /**
   * The automatic adoption on upload or attach failed after the link was
   * committed, so both lists are empty and the operator must run it by hand.
   * A manual adopt stores false, which clears the warning.
   */
  adoptionFailed?: boolean;
}

export interface UseSubsidyOfficeReportsForMonthParams {
  officeId: string | null;
  /** Bezugsjahr — the year the report LINES are for, not the tranche year. */
  year: number | null;
  /** Bezugsmonat, 1–12. */
  month: number | null;
}

/**
 * Lists the office's live reports that carry at least one line FOR the given
 * month, with only those lines. Disabled until an office and a month are
 * selected.
 */
export function useSubsidyOfficeReportsForMonth(
  params: UseSubsidyOfficeReportsForMonthParams,
) {
  const { orgId, isSignedIn } = useAuth();
  const enabled =
    Boolean(isSignedIn) &&
    params.officeId !== null &&
    params.officeId !== "" &&
    params.year !== null &&
    params.month !== null;

  return useQuery<reconciliation_SubsidyOfficeReportListResponse>({
    queryKey: subsidyOfficeReportKeys.forMonth(
      orgId ?? null,
      params.officeId,
      params.year,
      params.month,
    ),
    queryFn: () =>
      ReconciliationService.getApiV1ReconciliationSubsidyOfficeReports(
        params.officeId as string,
        params.year as number,
        params.month as number,
      ),
    enabled,
    staleTime: 30_000,
  });
}

/**
 * invalidateReportScopes refreshes both the reports themselves and the matching
 * tab. The two are always invalidated together: attaching a payment, assigning
 * a child or deleting a report all change what the overlay computes, and the
 * left rail's "Report" badge is derived from the report link.
 */
function invalidateReportScopes(queryClient: QueryClient) {
  queryClient.invalidateQueries({ queryKey: subsidyOfficeReportKeys.all });
  queryClient.invalidateQueries({ queryKey: subsidyMatchingKeys.all });
}

/**
 * storeAdoptionAndRefresh keeps the adoption result for the verdict card, then
 * refreshes. An adoption writes invoice subsidy corrections, so the money views
 * go first (awaited, so their cancel pass cannot race), then the report scopes.
 * The adoption entry is a disabled query, so the invalidation never refetches
 * it away.
 */
async function storeAdoptionAndRefresh(
  queryClient: QueryClient,
  orgId: string | null,
  reportId: string | undefined,
  result: Partial<reconciliation_SubsidyRoundingAdoptionResponse> & {
    adoptionFailed?: boolean;
  },
) {
  if (reportId) {
    const key = subsidyOfficeReportKeys.adoption(orgId, reportId);
    const adopted = result.adopted ?? [];
    const skipped = result.skipped ?? [];
    // A re-run after a complete run adopts nothing: every row is already
    // within tolerance. Storing that empty result would wipe the receipt of
    // the run that corrected the invoices, which is the only place the
    // operator can still see which invoices changed. Keep it.
    const previous = queryClient.getQueryData<SubsidyReportAdoption>(key);
    const keepReceipt =
      adopted.length === 0 &&
      skipped.length === 0 &&
      result.adoptionFailed !== true &&
      previous != null &&
      previous.adopted.length > 0;
    const adoption: SubsidyReportAdoption = {
      adopted: keepReceipt ? previous.adopted : adopted,
      skipped,
      adoptionFailed: result.adoptionFailed === true,
    };
    queryClient.setQueryData<SubsidyReportAdoption>(key, adoption);
  }
  await invalidateMoneyViews(queryClient);
  invalidateReportScopes(queryClient);
}

export interface UploadSubsidyOfficeReportInput {
  file: File;
  officeId: string;
  /** Link the report to this payment; omitted lets the server decide. */
  paymentId?: string;
}

export function useUploadSubsidyOfficeReport() {
  const queryClient = useQueryClient();
  const { orgId } = useAuth();
  return useMutation<
    reconciliation_SubsidyOfficeReportDetailResponse,
    unknown,
    UploadSubsidyOfficeReportInput
  >({
    mutationFn: ({ file, officeId, paymentId }) =>
      ReconciliationService.postApiV1ReconciliationSubsidyOfficeReports(
        file,
        officeId,
        paymentId,
      ),
    onSuccess: async (data) => {
      await storeAdoptionAndRefresh(
        queryClient,
        orgId ?? null,
        data.report?.id,
        data,
      );
    },
  });
}

export interface SetSubsidyOfficeReportLineChildInput {
  reportId: string;
  lineId: string;
  childId: string;
}

export function useSetSubsidyOfficeReportLineChild() {
  const queryClient = useQueryClient();
  return useMutation<
    reconciliation_SubsidyOfficeReportDetailResponse,
    unknown,
    SetSubsidyOfficeReportLineChildInput
  >({
    mutationFn: ({ reportId, lineId, childId }) =>
      ReconciliationService.patchApiV1ReconciliationSubsidyOfficeReportsLines(
        reportId,
        lineId,
        { childId },
      ),
    onSuccess: () => invalidateReportScopes(queryClient),
  });
}

export interface AttachSubsidyOfficeReportPaymentInput {
  reportId: string;
  /** Empty string detaches, which returns the report to audit mode. */
  paymentId: string;
}

export function useAttachSubsidyOfficeReportPayment() {
  const queryClient = useQueryClient();
  const { orgId } = useAuth();
  return useMutation<
    reconciliation_SubsidyOfficeReportDetailResponse,
    unknown,
    AttachSubsidyOfficeReportPaymentInput
  >({
    mutationFn: ({ reportId, paymentId }) =>
      ReconciliationService.postApiV1ReconciliationSubsidyOfficeReportsAttachPayment(
        reportId,
        { paymentId },
      ),
    onSuccess: async (data, { reportId }) => {
      await storeAdoptionAndRefresh(
        queryClient,
        orgId ?? null,
        data.report?.id ?? reportId,
        data,
      );
      invalidateSubsidyQueue(queryClient);
    },
  });
}

export interface AdoptSubsidyReportRoundingInput {
  reportId: string;
}

/**
 * useAdoptSubsidyReportRounding wraps
 * POST /reconciliation/subsidy-office-reports/:id/adopt-rounding (issue #1576):
 * adopt the office's rounded amounts on a statement linked before the setting
 * was turned on.
 */
export function useAdoptSubsidyReportRounding() {
  const queryClient = useQueryClient();
  const { orgId } = useAuth();
  const { i18n } = useLingui();
  return useMutation<
    reconciliation_SubsidyRoundingAdoptionResponse,
    unknown,
    AdoptSubsidyReportRoundingInput
  >({
    mutationFn: ({ reportId }) =>
      ReconciliationService.postApiV1ReconciliationSubsidyOfficeReportsAdoptRounding(
        reportId,
      ),
    onSuccess: async (data, { reportId }) => {
      await storeAdoptionAndRefresh(queryClient, orgId ?? null, reportId, data);
      invalidateSubsidyQueue(queryClient);
    },
    onError: (error) => {
      toast.error(
        backendErrorText(
          error,
          i18n,
          msg({
            id: "reconciliation.subsidy.report.adoption.failed",
            message: "Couldn't adopt the office amounts. Please try again.",
          }),
        ),
      );
    },
  });
}

/**
 * useSubsidyReportAdoption reads the adoption result stored for a statement by
 * the upload, attach and adopt mutations. It never fetches: there is no GET for
 * it, so the query is disabled and starts at null.
 */
export function useSubsidyReportAdoption(reportId: string | null | undefined) {
  const { orgId } = useAuth();
  const queryClient = useQueryClient();
  const queryKey = subsidyOfficeReportKeys.adoption(
    orgId ?? null,
    reportId ?? "",
  );
  return useQuery<SubsidyReportAdoption | null>({
    queryKey,
    queryFn: () =>
      queryClient.getQueryData<SubsidyReportAdoption | null>(queryKey) ?? null,
    enabled: false,
    initialData: null,
    staleTime: Infinity,
  });
}

export function useDeleteSubsidyOfficeReport() {
  const queryClient = useQueryClient();
  return useMutation<void, unknown, string>({
    mutationFn: (reportId) =>
      ReconciliationService.deleteApiV1ReconciliationSubsidyOfficeReports(
        reportId,
      ),
    onSuccess: () => invalidateReportScopes(queryClient),
  });
}
